import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.css';
import { useTheme } from '@/contexts/ThemeContext';
import { layout4ThemeStyle } from './theme';
import { useAppData } from '@/contexts/AppDataContext';
import { generateVehicleSlug } from '@/lib/slug';
import { useStore } from '@/contexts/StoreContext';
import { useIsMobile } from '@/hooks/use-mobile';

type VehicleTab = 'all' | 'seminovo' | 'zerokm' | 'blindados';

interface HeroProps {
  config: any;
}

export const Hero = ({ config }: HeroProps) => {
  const { selectedStore, availableStores } = useStore();
  const store = selectedStore || config.stores[0];
  const isMobile = useIsMobile();
  const banners = useMemo(
    () => store.banners?.filter((v: any) => v.platform === (isMobile ? 'mobile' : 'web')) || [],
    [store.banners, isMobile]
  );
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<VehicleTab>('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedModel, setSelectedModel] = useState('all');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000000]);
  const navigate = useNavigate();
  const { colors } = useTheme();
  const themeStyle = layout4ThemeStyle(colors);
  const { vehicles } = useAppData();
  
  // Filter vehicles based on unifiedAds and unifiedAdsStore rules
  const filteredVehicles = useMemo(() => {
    const unifiedAds = config?.unifiedAds !== false;
    const unifiedAdsStore = config?.unifiedAdsStore;
    const selectedStoreId = selectedStore?._id;
    
    // Rule 1: If unifiedAds is false, always filter by selected store
    if (!unifiedAds) {
      if (selectedStoreId) {
        return vehicles.filter(v => v.store === selectedStoreId);
      }
      return vehicles;
    }
    
    // Rule 2: If unifiedAds is true and unifiedAdsStore is null, show all vehicles
    if (unifiedAdsStore === null || unifiedAdsStore === undefined) {
      return vehicles;
    }
    
    // Rule 3 & 4: If unifiedAds is true and unifiedAdsStore is set
    if (selectedStoreId === unifiedAdsStore) {
      // Rule 3: Selected store is the unified store → show all vehicles
      return vehicles;
    } else {
      // Rule 4: Selected store is different → filter by selected store
      if (selectedStoreId) {
        return vehicles.filter(v => v.store === selectedStoreId);
      }
      return vehicles;
    }
  }, [config?.unifiedAds, config?.unifiedAdsStore, vehicles, selectedStore?._id]);

  const buttonColor = colors.button;

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(price);

  const tabVehicles = useMemo(() => {
    if (activeTab === 'zerokm') {
      return filteredVehicles.filter(v => v.isZeroKm || v.mileage === 0);
    }
    if (activeTab === 'seminovo') {
      return filteredVehicles.filter(v => !v.isZeroKm && v.mileage > 0 && !v.armored);
    }
    if (activeTab === 'blindados') {
      return filteredVehicles.filter(v => v.armored);
    }
    return filteredVehicles;
  }, [filteredVehicles, activeTab]);

  const categoryAvailability = useMemo(() => ({
    seminovo:
      !!store?.ad?.showUsed &&
      filteredVehicles.some(v => !v.isZeroKm && v.mileage > 0 && !v.armored),
    zerokm:
      !!store?.ad?.showZeroKm &&
      filteredVehicles.some(v => v.isZeroKm || v.mileage === 0),
    blindados:
      !!store?.ad?.showArmored &&
      filteredVehicles.some(v => v.armored),
  }), [filteredVehicles, store?.ad?.showUsed, store?.ad?.showZeroKm, store?.ad?.showArmored]);

  const vehicleTabs = useMemo(() => {
    const categoryTabs: { id: VehicleTab; label: string }[] = [];

    if (categoryAvailability.seminovo) {
      categoryTabs.push({ id: 'seminovo', label: 'Seminovos' });
    }
    if (categoryAvailability.zerokm) {
      categoryTabs.push({ id: 'zerokm', label: 'Zero KM' });
    }
    if (categoryAvailability.blindados) {
      categoryTabs.push({ id: 'blindados', label: 'Blindados' });
    }

    if (categoryTabs.length >= 2) {
      return [{ id: 'all', label: 'Todos os Carros' }, ...categoryTabs];
    }

    if (categoryTabs.length === 1) {
      return categoryTabs;
    }

    if (filteredVehicles.length > 0) {
      return [{ id: 'all', label: 'Todos os Carros' }];
    }

    return [];
  }, [categoryAvailability, filteredVehicles.length]);

  useEffect(() => {
    if (vehicleTabs.length === 0) return;
    if (!vehicleTabs.some(tab => tab.id === activeTab)) {
      setActiveTab(vehicleTabs[0].id);
      setSelectedBrand('all');
      setSelectedModel('all');
    }
  }, [vehicleTabs, activeTab]);

  const priceBounds = useMemo(() => {
    if (tabVehicles.length === 0) return { min: 0, max: 1000000 };
    const prices = tabVehicles.map(v => v.price);
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [tabVehicles]);

  useEffect(() => {
    setPriceRange([priceBounds.min, priceBounds.max]);
  }, [priceBounds.min, priceBounds.max, activeTab]);

  const brands = useMemo(
    () => Array.from(new Set(tabVehicles.map(v => v.brand))).sort(),
    [tabVehicles]
  );

  const models = useMemo(() => {
    const source = selectedBrand === 'all'
      ? tabVehicles
      : tabVehicles.filter(v => v.brand === selectedBrand);
    return Array.from(new Set(source.map(v => v.model))).sort();
  }, [tabVehicles, selectedBrand]);

  const matchCount = useMemo(() =>
    tabVehicles.filter(v => {
      if (selectedBrand !== 'all' && v.brand !== selectedBrand) return false;
      if (selectedModel !== 'all' && v.model !== selectedModel) return false;
      return v.price >= priceRange[0] && v.price <= priceRange[1];
    }).length,
    [tabVehicles, selectedBrand, selectedModel, priceRange]
  );

  const handleTabChange = (tab: VehicleTab) => {
    setActiveTab(tab);
    setSelectedBrand('all');
    setSelectedModel('all');
  };

  const handleBrandChange = (brand: string) => {
    setSelectedBrand(brand);
    setSelectedModel('all');
  };

  const handleMotorxSearch = () => {
    const params = new URLSearchParams();
    if (activeTab === 'zerokm') params.set('type', 'zerokm');
    if (activeTab === 'seminovo') params.set('type', 'seminovo');
    if (activeTab === 'blindados') params.set('armored', 'true');
    if (selectedBrand !== 'all') params.set('brand', selectedBrand);
    if (selectedModel !== 'all') params.set('model', selectedModel);
    if (priceRange[0] > priceBounds.min) params.set('minPrice', String(priceRange[0]));
    if (priceRange[1] < priceBounds.max) params.set('maxPrice', String(priceRange[1]));
    const query = params.toString();
    navigate(query ? `/estoque?${query}` : '/estoque');
  };

  // Reset banner index when store or banner list changes
  useEffect(() => {
    setCurrentBannerIndex(0);
  }, [store._id, banners.length]);

  // Auto-rotate through all banners
  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    }, 9000);

    return () => clearInterval(interval);
  }, [banners.length]);

  const handleBannerChange = (index: number) => {
    if (index >= 0 && index < banners.length) {
      setCurrentBannerIndex(index);
    }
  };

  const isBannerClickable = (banner: any) =>
    banner?.type && banner.type !== 'none' && banner.type !== 'null' && banner.type !== null;

  const handleBannerClick = async (banner: any) => {
    if (!banner || !banner.type) return;

    switch (banner.type) {
      case 'ad':
        // Check number of values
        if (banner.values && banner.values.length > 0) {
          if (banner.values.length === 1) {
            // Single vehicle - redirect to vehicle detail with slug
            const vehicleId = banner.values[0].value;
            const vehicle = vehicles.find(v => v.id.toString() === vehicleId.toString());
            if (vehicle) {
              const slug = await generateVehicleSlug(vehicle);
              navigate(`/estoque/${slug}`);
            } else {
              navigate(`/estoque/${vehicleId}`); // Fallback to ID
            }
          } else {
            // Multiple vehicles - redirect to banner vehicles page
            const vehicleIds = banner.values.map((v: any) => v.value).join(',');
            const title = encodeURIComponent(banner.title || 'Veículos em Promoção');
            navigate(`/destaques?ids=${vehicleIds}&title=${title}`);
          }
        }
        break;

      case 'link':
        // Open external link
        if (banner.values && banner.values.length > 0 && banner.values[0].value) {
          window.open(banner.values[0].value, '_blank');
        }
        break;

      case null:
      case 'null':
        // No action
        break;

      default:
        break;
    }
  };

  const hasBanners = banners.length > 0;

  return (
    <>
      {hasBanners && (
        <section
          id="inicio"
          className="wl4-hero"
          style={themeStyle}
        >
          <div className={`wl4-hero-banner full-banner relative ${isMobile ? 'wl4-hero-banner--mobile full-banner--mobile' : 'wl4-hero-banner--desktop full-banner--desktop full-banner--card full-banner--layout4'}`}>
            {!isMobile && (
              <div className="full-banner__blur" aria-hidden="true">
                {banners.map((banner: any, index: number) => (
                  <img
                    key={`blur-${banner.image}-${index}`}
                    src={banner.image}
                    alt=""
                    className={`full-banner__blur-img transition-opacity duration-1000 ease-in-out ${
                      index === currentBannerIndex ? 'opacity-100 z-[1]' : 'opacity-0 z-0'
                    }`}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    draggable={false}
                  />
                ))}
                <div className="full-banner__blur-overlay" />
              </div>
            )}
            <div className="full-banner__frame">
            {banners.map((banner: any, index: number) => (
              <button
                key={`${banner.image}-${index}`}
                type="button"
                className={`wl4-hero-banner-slide transition-opacity duration-1000 ease-in-out ${
                  index === currentBannerIndex ? 'opacity-100 z-[1]' : 'opacity-0 z-0'
                } ${isBannerClickable(banner) ? 'cursor-pointer' : 'cursor-default'}`}
                onClick={() => isBannerClickable(banner) && handleBannerClick(banner)}
                aria-label={banner.title || `Banner ${index + 1}`}
                aria-hidden={index !== currentBannerIndex}
                tabIndex={index === currentBannerIndex ? 0 : -1}
              >
                <img
                  src={banner.image}
                  alt={banner.title || `Banner ${index + 1}`}
                  className="wl4-hero-banner-img"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  draggable={false}
                />
              </button>
            ))}
            {banners.length > 1 && (
              <>
                <button
                  type="button"
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/95 border border-[var(--wl4-border)] shadow-sm hover:shadow-md transition-shadow hidden md:flex items-center justify-center"
                  onClick={() => handleBannerChange(currentBannerIndex === 0 ? banners.length - 1 : currentBannerIndex - 1)}
                  aria-label="Banner anterior"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/95 border border-[var(--wl4-border)] shadow-sm hover:shadow-md transition-shadow hidden md:flex items-center justify-center"
                  onClick={() => handleBannerChange((currentBannerIndex + 1) % banners.length)}
                  aria-label="Próximo banner"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </>
            )}
            </div>
          </div>
        </section>
      )}

      {/* Busca — fundo branco */}
      <section
        id={hasBanners ? undefined : 'inicio'}
        className={`wl4-home-intro${hasBanners ? '' : ' wl4-home-intro--no-banner'}`}
        style={themeStyle}
        aria-label="Busca de veículos"
      >
        <div className="wl4-container">
          <div className="wl4-motorx-search">
            {vehicleTabs.length > 1 && (
              <div className="wl4-motorx-tabs" role="tablist" aria-label="Tipo de veículo">
                {vehicleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={`wl4-motorx-tab ${activeTab === tab.id ? 'is-active' : ''}`}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            <div className={`wl4-motorx-search-bar ${vehicleTabs.length <= 1 ? 'wl4-motorx-search-bar--no-tabs' : ''}`}>
              <div className="wl4-motorx-field">
                <label htmlFor="hero-brand">Marca</label>
                <Select value={selectedBrand} onValueChange={handleBrandChange}>
                  <SelectTrigger id="hero-brand" className="wl4-motorx-select">
                    <SelectValue placeholder="Marca" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as marcas</SelectItem>
                    {brands.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="wl4-motorx-field">
                <label htmlFor="hero-model">Modelo</label>
                <Select
                  value={selectedModel}
                  onValueChange={setSelectedModel}
                  disabled={models.length === 0}
                >
                  <SelectTrigger id="hero-model" className="wl4-motorx-select">
                    <SelectValue placeholder="Modelo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os modelos</SelectItem>
                    {models.map(model => (
                      <SelectItem key={model} value={model}>{model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="wl4-motorx-field wl4-motorx-field--price">
                <label>Preço</label>
                <div className="wl4-motorx-price">
                  {priceBounds.max > priceBounds.min ? (
                    <>
                      <Slider
                        min={priceBounds.min}
                        max={priceBounds.max}
                        step={Math.max(1000, Math.round((priceBounds.max - priceBounds.min) / 100))}
                        value={priceRange}
                        onValueChange={(value) => setPriceRange([value[0], value[1]])}
                        className="wl4-motorx-slider"
                      />
                      <div className="wl4-motorx-price-labels">
                        <span>{formatPrice(priceRange[0])}</span>
                        <span>{formatPrice(priceRange[1])}</span>
                      </div>
                    </>
                  ) : (
                    <div className="wl4-motorx-price-labels !mt-0">
                      <span>{formatPrice(priceBounds.min)}</span>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="button"
                className="wl4-motorx-search-btn"
                style={{ backgroundColor: buttonColor }}
                onClick={handleMotorxSearch}
              >
                <Search className="h-5 w-5" />
                <span>{matchCount} {matchCount === 1 ? 'Veículo' : 'Veículos'}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

    </>
  );
};